import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Memory,
  Warning,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, Component, ComponentCreate } from '../../services/simpleApi';

const ComponentsPage: React.FC = () => {
  const theme = useTheme();
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingComponent, setEditingComponent] = useState<Component | null>(null);
  const [formData, setFormData] = useState<ComponentCreate>({
    name: '',
    category: 'electronics',
    unit_of_measure: 'units',
    unit_cost: 0,
    current_stock: 0,
    safety_stock: 10,
    reorder_point: 20,
    lead_time_days: 14,
    is_critical: false,
  });

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const response = await simpleApi.components.list();
      setComponents(response.items);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load components');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComponents();
  }, []);

  const handleOpenDialog = (component?: Component) => {
    if (component) {
      setEditingComponent(component);
      setFormData({
        name: component.name,
        component_id: component.component_id,
        description: component.description,
        category: component.category,
        unit_of_measure: component.unit_of_measure,
        unit_cost: component.unit_cost,
        current_stock: component.current_stock,
        safety_stock: component.safety_stock,
        reorder_point: component.reorder_point,
        lead_time_days: component.lead_time_days,
        primary_supplier_id: component.primary_supplier_id,
        country_of_origin: component.country_of_origin,
        hs_code: component.hs_code,
        is_critical: component.is_critical,
      });
    } else {
      setEditingComponent(null);
      setFormData({
        name: '',
        category: 'electronics',
        unit_of_measure: 'units',
        unit_cost: 0,
        current_stock: 0,
        safety_stock: 10,
        reorder_point: 20,
        lead_time_days: 14,
        is_critical: false,
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingComponent) {
        await simpleApi.components.update(editingComponent.id, formData);
        setSuccess('Component updated successfully');
      } else {
        await simpleApi.components.create(formData);
        setSuccess('Component created successfully');
      }
      setOpenDialog(false);
      fetchComponents();
    } catch (err: any) {
      setError(err.message || 'Failed to save component');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;
    try {
      await simpleApi.components.delete(id);
      setSuccess('Component deleted successfully');
      fetchComponents();
    } catch (err: any) {
      setError(err.message || 'Failed to delete component');
    }
  };

  const filteredComponents = components.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.component_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStockStatus = (component: Component) => {
    if (component.current_stock <= 0) return { color: 'error', icon: <Error />, label: 'Out of Stock' };
    if (component.current_stock < component.safety_stock) return { color: 'error', icon: <Warning />, label: 'Critical' };
    if (component.current_stock < component.reorder_point) return { color: 'warning', icon: <Warning />, label: 'Low Stock' };
    return { color: 'success', icon: <CheckCircle />, label: 'In Stock' };
  };

  const criticalCount = components.filter(c => c.is_critical).length;
  const lowStockCount = components.filter(c => c.current_stock < c.reorder_point).length;
  const totalValue = components.reduce((sum, c) => sum + (c.current_stock * c.unit_cost), 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Components
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage raw materials and components for manufacturing
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Component
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchComponents} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {components.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Components
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                {criticalCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical Components
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                {lowStockCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Low Stock Alerts
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                £{totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Inventory Value
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search components by name, ID, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Card>

      {/* Components Table */}
      <Card>
        {loading ? (
          <LoadingState message="Loading components..." />
        ) : filteredComponents.length === 0 ? (
          <EmptyState
            title="No components found"
            description="Add your first component to start managing your Bill of Materials"
            actionLabel="Add Component"
            onAction={() => handleOpenDialog()}
            icon={<Memory sx={{ fontSize: 64, color: 'text.disabled' }} />}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Component</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Lead Time</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredComponents.map((component) => {
                  const stockStatus = getStockStatus(component);
                  return (
                    <TableRow key={component.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              bgcolor: component.is_critical 
                                ? alpha(theme.palette.error.main, 0.1)
                                : alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Memory color={component.is_critical ? 'error' : 'primary'} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {component.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {component.component_id}
                              {component.is_critical && (
                                <Chip label="Critical" size="small" color="error" sx={{ ml: 1, height: 16 }} />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={component.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {component.current_stock} {component.unit_of_measure}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Reorder: {component.reorder_point}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={stockStatus.icon}
                          label={stockStatus.label}
                          size="small"
                          color={stockStatus.color as any}
                        />
                      </TableCell>
                      <TableCell>
                        {component.lead_time_days} days
                      </TableCell>
                      <TableCell align="right">
                        £{component.unit_cost.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(component)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(component.id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={() => setSuccess(null)}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingComponent ? 'Edit Component' : 'Add New Component'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Component Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Component ID"
                value={formData.component_id || ''}
                onChange={(e) => setFormData({ ...formData, component_id: e.target.value })}
                placeholder="Auto-generated if empty"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value="electronics">Electronics</MenuItem>
                  <MenuItem value="mechanical">Mechanical</MenuItem>
                  <MenuItem value="raw_materials">Raw Materials</MenuItem>
                  <MenuItem value="packaging">Packaging</MenuItem>
                  <MenuItem value="consumables">Consumables</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit of Measure"
                value={formData.unit_of_measure}
                onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Unit Cost (£)"
                type="number"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Current Stock"
                type="number"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Safety Stock"
                type="number"
                value={formData.safety_stock}
                onChange={(e) => setFormData({ ...formData, safety_stock: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Reorder Point"
                type="number"
                value={formData.reorder_point}
                onChange={(e) => setFormData({ ...formData, reorder_point: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Lead Time (days)"
                type="number"
                value={formData.lead_time_days}
                onChange={(e) => setFormData({ ...formData, lead_time_days: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country of Origin"
                value={formData.country_of_origin || ''}
                onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HS Code"
                value={formData.hs_code || ''}
                onChange={(e) => setFormData({ ...formData, hs_code: e.target.value })}
                placeholder="Harmonized System code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_critical}
                    onChange={(e) => setFormData({ ...formData, is_critical: e.target.checked })}
                    color="error"
                  />
                }
                label="Critical Component (affects production if unavailable)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingComponent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComponentsPage;
